/**
 * QuickActionsCard Component
 *
 * Shows quick action buttons based on current queue state.
 * Designed for fast barber workflow without opening menus.
 *
 * States:
 * - in_service: Shows "Finish" button + remaining time + "Finishing soon" option
 * - notified/arrived: Shows "Cutting!" quick start, "Mark arrival", etc.
 * - waiting: Shows "Call client"
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Scissors, UserCheck, Phone, Clock, CheckCircle2, Play, AlertTriangle, Crown, Calendar } from 'lucide-react';
import type { QueueTicket } from '../../types';
import { soundManager } from '../../services/QueueSoundManager';

const START_SERVICE_REMINDER_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes

interface QuickActionsCardProps {
  currentTicket: QueueTicket | null; // Ticket in service
  nextTicket: QueueTicket | null; // Next ticket in line (waiting/notified/arrived)
  onCallClient: (ticket: QueueTicket) => void;
  onMarkArrival: (queueId: string) => void;
  onStartService: (queueId: string) => void;
  onCompleteTicket: (queueId: string) => void;
  onFinishingSoon?: () => void; // Optional - kept for backwards compatibility
  processing: boolean;
}

export function QuickActionsCard({
  currentTicket,
  nextTicket,
  onCallClient,
  onMarkArrival,
  onStartService,
  onCompleteTicket,
  // onFinishingSoon - removed from UI but kept in props for backwards compatibility
  processing,
}: QuickActionsCardProps) {
  const { t } = useTranslation();
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const [arrivedElapsedMs, setArrivedElapsedMs] = useState(0);
  const lastAlertedTicketIdRef = useRef<string | null>(null);

  // Track time since the next ticket arrived (status === 'arrived')
  // to remind the barber to start the service.
  useEffect(() => {
    if (nextTicket?.status !== 'arrived' || !nextTicket?.arrivedAt) {
      setArrivedElapsedMs(0);
      return;
    }
    const arrivedAtMs = nextTicket.arrivedAt.toMillis();
    const update = () => setArrivedElapsedMs(Date.now() - arrivedAtMs);
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [nextTicket?.queueId, nextTicket?.status, nextTicket?.arrivedAt]);

  const startServiceOverdue =
    nextTicket?.status === 'arrived' &&
    arrivedElapsedMs >= START_SERVICE_REMINDER_THRESHOLD_MS;
  const overdueMinutes = Math.floor(arrivedElapsedMs / 60000);

  // Play warning sound once per ticket when threshold is crossed
  useEffect(() => {
    if (!startServiceOverdue || !nextTicket) return;
    if (lastAlertedTicketIdRef.current === nextTicket.queueId) return;
    lastAlertedTicketIdRef.current = nextTicket.queueId;
    soundManager.playSound('warning').catch(() => {
      // Ignore sound failures (e.g. user hasn't interacted with page yet)
    });
    // Best-effort browser notification — works while the tab is open or in background
    if (typeof Notification !== 'undefined') {
      try {
        if (Notification.permission === 'granted') {
          new Notification('⚠️ Inicia el servicio', {
            body: `${nextTicket.clientName || 'Cliente'} lleva esperando ${overdueMinutes} min`,
            tag: `start-service-${nextTicket.queueId}`,
            requireInteraction: false,
          });
        } else if (Notification.permission === 'default') {
          Notification.requestPermission().catch(() => {});
        }
      } catch {
        // Notification API not available
      }
    }
  }, [startServiceOverdue, nextTicket?.queueId]);

  // Calculate remaining time for current service
  useEffect(() => {
    if (!currentTicket?.serviceStartedAt) {
      setRemainingMinutes(0);
      return;
    }

    const calculateRemaining = () => {
      const startTime = currentTicket.serviceStartedAt!.toMillis();
      const elapsed = Math.floor((Date.now() - startTime) / (1000 * 60));
      const duration = currentTicket.serviceDuration || 30;
      setRemainingMinutes(Math.max(0, duration - elapsed));
    };

    calculateRemaining();
    const interval = setInterval(calculateRemaining, 60000);
    return () => clearInterval(interval);
  }, [currentTicket?.serviceStartedAt, currentTicket?.serviceDuration]);

  // Quick action: Mark arrival + Start service immediately
  const handleQuickStart = async (ticket: QueueTicket) => {
    onMarkArrival(ticket.queueId);
    // Small delay to ensure arrival is marked before starting
    setTimeout(() => onStartService(ticket.queueId), 600);
  };

  // Don't render if nothing to show
  if (!currentTicket && !nextTicket) {
    return null;
  }

  return (
    <div className="bg-surface border border-hairline rounded-xl p-4 mb-6">
      <h3 className="text-xs font-medium text-ink-subtle uppercase tracking-wide mb-3 flex items-center gap-2">
        <Scissors size={14} />
        {t('queueManagement.quickActions.title', 'Acciones Rápidas')}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* LEFT SIDE: Current client in service */}
        {currentTicket && (
          <div className="bg-surface-2 rounded-lg p-4 border border-hairline">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
                  {t('queueManagement.quickActions.currentlyServing', 'Atendiendo')}
                </span>
                <p className="text-lg font-semibold text-ink">
                  {currentTicket.isCompanion && currentTicket.companionName
                    ? currentTicket.companionName
                    : currentTicket.clientName || `#${currentTicket.ticketNumber}`
                  }
                </p>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold tabular-nums ${
                  remainingMinutes <= 5
                    ? 'bg-danger/10 text-danger'
                    : remainingMinutes <= 10
                    ? 'bg-warning/10 text-warning'
                    : 'bg-surface-3 text-ink-muted'
                }`}>
                  <Clock size={14} />
                  {remainingMinutes}min
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              {/* Complete service — primary positive action */}
              <button
                onClick={() => onCompleteTicket(currentTicket.queueId)}
                disabled={processing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-lg transition-colors"
              >
                <CheckCircle2 size={18} />
                <span>{t('queueManagement.quickActions.finish', 'Terminar')}</span>
              </button>
            </div>
          </div>
        )}

        {/* RIGHT SIDE: Next client actions */}
        {nextTicket && (
          <div className={`bg-surface-2 rounded-lg p-4 border border-hairline ${
            startServiceOverdue
              ? 'ring-2 ring-danger ring-offset-2 ring-offset-canvas border-danger'
              : ''
          }`}>
            {startServiceOverdue && (
              <div className="mb-3 -mx-4 -mt-4 px-4 py-2 bg-danger rounded-t-lg flex items-center justify-center gap-2 text-white font-bold uppercase tracking-wide text-sm">
                <AlertTriangle size={18} />
                <span>Inicia el servicio · {overdueMinutes} min esperando</span>
              </div>
            )}
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className={`text-xs font-medium uppercase tracking-wide ${
                  nextTicket.status === 'arrived' ? 'text-success' : 'text-accent'
                }`}>
                  {nextTicket.status === 'waiting'
                    ? t('queueManagement.quickActions.nextUp', 'Siguiente')
                    : nextTicket.status === 'notified'
                    ? t('queueManagement.quickActions.called', 'Llamado')
                    : t('queueManagement.quickActions.arrived', 'Ha llegado')
                  }
                </span>
                <p className="text-lg font-semibold text-ink">
                  {nextTicket.isCompanion && nextTicket.companionName
                    ? nextTicket.companionName
                    : nextTicket.clientName || `#${nextTicket.ticketNumber}`
                  }
                </p>
              </div>
              <span className="text-2xl font-semibold text-ink-subtle tabular-nums">
                #{nextTicket.ticketNumber}
              </span>
            </div>

            <div className="flex gap-2 flex-wrap">
              {/* WAITING: Call client */}
              {nextTicket.status === 'waiting' && (
                <button
                  onClick={() => onCallClient(nextTicket)}
                  disabled={processing}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-lg transition-colors"
                >
                  <Phone size={18} />
                  <span>{t('queueManagement.quickActions.call', 'Llamar')}</span>
                </button>
              )}

              {/* NOTIFIED: Quick start (cutting) or mark arrival */}
              {nextTicket.status === 'notified' && (
                <>
                  <button
                    onClick={() => handleQuickStart(nextTicket)}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-lg transition-colors min-w-[120px]"
                  >
                    <Scissors size={18} />
                    <span>{t('queueManagement.quickActions.cutting', 'Cortando')}</span>
                  </button>
                  <button
                    onClick={() => onMarkArrival(nextTicket.queueId)}
                    disabled={processing}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-surface-3 border border-hairline text-ink hover:bg-surface disabled:opacity-50 font-semibold rounded-lg transition-colors"
                  >
                    <UserCheck size={18} />
                    <span>{t('queueManagement.quickActions.markArrival', 'Llegó')}</span>
                  </button>
                </>
              )}

              {/* ARRIVED: Start service */}
              {nextTicket.status === 'arrived' && (
                <>
                  <button
                    onClick={() => onStartService(nextTicket.queueId)}
                    disabled={processing}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-accent text-on-accent hover:bg-accent-hover disabled:opacity-50 font-bold rounded-lg transition-colors"
                  >
                    <Play size={18} />
                    <span>{t('queueManagement.quickActions.startService', 'Iniciar')}</span>
                  </button>
                  <button
                    onClick={() => handleQuickStart(nextTicket)}
                    disabled={processing}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-surface-3 border border-hairline text-ink hover:bg-surface disabled:opacity-50 font-semibold rounded-lg transition-colors"
                  >
                    <Scissors size={18} />
                    <span>{t('queueManagement.quickActions.cutting', 'Cortando')}</span>
                  </button>
                </>
              )}
            </div>

            {/* VIP / Appointment badges */}
            {((nextTicket as any).isVIP || (nextTicket as any).isAppointment) && (
              <div className="flex gap-2 mt-3">
                {(nextTicket as any).isVIP && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/30">
                    <Crown className="w-3 h-3" aria-hidden="true" /> VIP
                  </span>
                )}
                {(nextTicket as any).isAppointment && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-info/10 text-info border border-info/30">
                    <Calendar className="w-3 h-3" aria-hidden="true" /> {t('queueManagement.barberInterface.appointment', 'Cita')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {/* If only current ticket (no next), span full width */}
        {currentTicket && !nextTicket && (
          <div className="bg-surface-2 border border-hairline rounded-lg p-4 flex items-center justify-center text-ink-subtle">
            <span className="text-sm">{t('queueManagement.quickActions.noNextClient', 'Sin clientes en espera')}</span>
          </div>
        )}
      </div>
    </div>
  );
}
